// 路由处理相关的工具函数
import _ from 'lodash'
import { toArray } from 'tree-lodash'
import {
  isExternal,
  removeLeadingSlash,
  addLeadingSlash,
  pathTrim,
  removeEndSlash,
  getFirstChildrenFields
} from './index'
import { MenuTypeEnums } from './enums'
import Layout from '@/layout/index.vue'
import MiddleDirectory from '@/layout/components/MiddleDirectory.vue'

/**
 * 验证路由节点数据的有效性
 * @param {Object} node 路由节点
 * @returns {boolean} 是否有效
 */
export function isValidRouteNode(node) {
  return node &&
         typeof node === 'object' &&
         node.id &&
         node.path !== undefined
}

/**
 * 清理路由路径
 * @param {string} path 原始路径
 * @returns {string} 清理后的路径
 */
export function sanitizePath(path) {
  if (!path) {
    return ''
  }
  return String(path).replace(/^\/+/, '').replace(/\/+$/, '')
}

/**
 * 构建完整路径
 * @param {string} parentPath 父路径
 * @param {string} currentPath 当前路径
 * @returns {string} 完整路径
 */
export function buildFullPath(parentPath, currentPath) {
  if (isExternal(currentPath)) {
    return currentPath
  }

  const cleanParent = (parentPath || '').replace(/\/+$/, '')
  const cleanCurrent = sanitizePath(currentPath)
  const absoluteCurrent = `/${cleanCurrent}`

  if (!cleanParent) {
    return absoluteCurrent
  }

  // 兼容后端菜单将子路由保存为包含父路径的完整路径。
  // 例如父路径 /market-insight、子路径 /market-insight/articles
  // 应仍然得到 /market-insight/articles，不能再拼接一次父路径。
  if (absoluteCurrent === cleanParent || absoluteCurrent.startsWith(`${cleanParent}/`)) {
    return removeEndSlash(absoluteCurrent)
  }

  return removeEndSlash(`${cleanParent}/${cleanCurrent}`)
}

/**
 * 创建路由元数据
 * @param {Object} item 菜单项
 * @returns {Object} 路由元数据
 */
export function createRouteMeta(item) {
  return {
    title: item.menuName || '',
    icon: item.menuIcon || '',
    affix: Boolean(item.affix),
    isChildMeta: false,
    permission: item.permission || '',
    keepAlive: Boolean(item.cache),
    dot: Boolean(item.dot),
    badge: item.badge || '',
    showHeader: Boolean(item.showHeader),
    showFooter: Boolean(item.showFooter),
    alwaysShow: Boolean(item.alwaysShow),
    sort: item.sortNum || 0,
    embed: Boolean(item.embed),
    embedUrl: item.embedUrl || '',
    hidden: !item.visible,
  }
}

/**
 * 格式化菜单项为路由项
 * @param {Object} item 菜单项
 * @returns {Object|null} 路由项
 */
export function formatMenuToRoute(item) {
  if (!isValidRouteNode(item)) {
    return null
  }

  return {
    id: item.id,
    parentId: item.parentId,
    menuType: item.menuType,
    componentPath: item.component,
    hidden: !item.visible,
    fullPath: null,
    path: item.path || '',
    name: item.componentName,
    meta: createRouteMeta(item),
    children: []
  }
}

/**
 * 处理目录节点
 * @param {Object} node 节点数据
 * @param {Object} parentNode 父节点数据
 * @returns {Object} 处理后的节点
 */
export function processDirectoryNode(node, parentNode) {
  if (!parentNode) {
    // 根目录节点
    node.component = Layout
  } else {
    // 中间目录节点
    node.path = removeLeadingSlash(node.path)
    node.component = MiddleDirectory
  }

  // redirect 到第一个子菜单
  const condition = (n) => n.menuType !== MenuTypeEnums.MENU.value
  const firstChildPath = getFirstChildrenFields(node, { fieldKey: 'path', condition }).join('/')
  node.redirect = pathTrim(addLeadingSlash(firstChildPath))

  // 目录下只有一个子节点时，菜单栏直接显示子节点内容
  const isSingleChild = node.children.length === 1
  if (isSingleChild && !node.meta.alwaysShow) {
    const child = node.children[0]
    node.meta = { ...child.meta }
    node.meta.isChildMeta = true
  }

  return node
}

/**
 * 处理菜单节点
 * @param {Object} node 节点数据
 * @param {Object} parentNode 父节点数据
 * @returns {Object} 处理后的节点
 */
export function processMenuNode(node, parentNode) {
  if (isExternal(node.path)) {
    // 外链处理
    node.path = removeLeadingSlash(node.path, true)
  } else if (!parentNode) {
    // 根节点为菜单，包装 Layout
    return wrapMenuWithLayout(node)
  } else {
    // 普通菜单节点
    const cleanNodePath = removeLeadingSlash(node.path)
    const cleanParentPath = removeLeadingSlash(parentNode.fullPath)
    node.path = cleanNodePath.startsWith(`${cleanParentPath}/`)
      ? cleanNodePath.slice(cleanParentPath.length + 1)
      : cleanNodePath
    node.component = loadComponent(node)
  }

  return node
}

/**
 * 将菜单节点包装在Layout中
 * @param {Object} node 菜单节点
 * @returns {Object} 包装后的节点
 */
export function wrapMenuWithLayout(node) {
  const childNode = _.cloneDeep(node)

  // 清理父节点属性
  delete node.name
  node.path = addLeadingSlash(node.path)
  node.redirect = node.path
  node.component = Layout

  // 设置子节点属性
  childNode.path = ''
  childNode.children = []

  // 处理所有子节点
  node.children = [childNode, ...(node.children || [])].map(item => ({
    ...item,
    component: loadComponent(item)
  }))

  return node
}

/**
 * 组件加载函数
 * @param {Object} menu 菜单项
 * @returns {Object} 组件
 */
export function loadComponent(menu) {
  // 内嵌iframe
  if (_.get(menu, 'meta.embed') === true) {
    return () => import('@/layout/components/InnerIframe/index.vue')
  }

  // 外链不需要组件
  if (isExternal(menu.path)) {
    return null
  }

  // 没有组件路径
  if (!menu.componentPath) {
    return null
  }

  // 动态加载组件
  return () => import(`@/views/${removeLeadingSlash(menu.componentPath)}`)
}

/**
 * 获取和指定菜单匹配度最高的菜单
 * @param {string} activeMenu 指定菜单路径
 * @param {Array} menuList 菜单列表
 * @returns {string|null} 匹配度最高的path
 */
export function getMaxMatchedMenu(activeMenu, menuList) {
  // 参数验证
  if (!activeMenu || !Array.isArray(menuList) || menuList.length === 0) {
    return null
  }

  // 预处理：获取所有有效的菜单路径
  const validMenuPaths = toArray(menuList)
    .filter(item => item && item.fullPath)
    .map(item => item.fullPath)

  if (validMenuPaths.length === 0) {
    return null
  }

  // 找到匹配的菜单并计算匹配度
  let bestMatch = null
  let maxSegments = 0

  for (const path of validMenuPaths) {
    if (isMenuSegmentMatch(activeMenu, path)) {
      const segments = path.split('/').length
      if (segments > maxSegments) {
        maxSegments = segments
        bestMatch = path
      }
    }
  }

  return bestMatch
}

/**
 * 匹配两个菜单路径
 * @param {string} curMenu 当前菜单路径
 * @param {string} matchMenu 匹配菜单路径
 * @returns {boolean} 是否匹配
 */
export function isMenuSegmentMatch(curMenu, matchMenu) {
  // 参数验证
  if (!curMenu || !matchMenu || typeof curMenu !== 'string' || typeof matchMenu !== 'string') {
    return false
  }

  // 如果当前菜单不是以匹配菜单开头，直接返回false
  if (!curMenu.startsWith(matchMenu)) {
    return false
  }

  // 分割路径进行比较
  const curParts = curMenu.split('/')
  const matchParts = matchMenu.split('/')

  // 匹配菜单必须是当前菜单的父级路径
  if (matchParts.length === curParts.length) {
    return false
  }

  // 检查匹配菜单的每个部分是否与当前菜单对应部分一致
  return matchParts.every((part, index) => part === curParts[index])
}
